import { nbsp } from '@ipfabric/frontend-shared-assets/utils/characters';
import { KeyboardKey } from 'components/KeyboardKey/KeyboardKey';
import type { ReactElement } from 'react';
import type { FC, ReactNode, ReactElement as reactElement1 } from 'react';

const isKeyboardKeyNode = (node: ReactNode): node is reactElement1 =>
  !!node && (node as ReactElement).type === KeyboardKey;

const surroundKeysWithSpaces = (children: ReactNode[]): ReactNode[] => {
  const result: ReactNode[] = [];
  children.forEach((child, index) => {
    if (!isKeyboardKeyNode(child)) {
      // pass through other nodes as they are
      result.push(child);
      return;
    }
    // surround KeyboardKey nodes inside text with extra space
    if (index > 0) {
      result.push(`${nbsp} `);
    }
    result.push(child);
    if (index < children.length - 1) {
      result.push(` ${nbsp}`);
    }
  });
  return result;
};

/**
 * Format text containing KeyboardKey elements to look good
 *
 * We need to enlarge spacing around the KeyboardKey elements to look good, but not
 * when it is first or last element.
 * I didn't find a way to do this with styles, giving this element a margin would cause it
 * to be spaced even when it occurs as a first or last child. Using :first-child and :last-child
 * doesn't help too since these pseudoselectors ignore text nodes
 * (so span in `<div>foo<span>bar</span>baz</div>` is both first and last child).
 */

interface TextWithKeyboardKeysProps {
  children: ReactNode;
}

export const TextWithKeyboardKeys: FC<TextWithKeyboardKeysProps> = ({
  children,
}) => {
  if (!Array.isArray(children)) {
    return <>{children}</>;
  }

  return <>{surroundKeysWithSpaces(children)}</>;
};
